/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from "@reduxjs/toolkit";
import { MVisionAppClient } from "../store/configurationTarget/mvision-client-list";
import { ModelType } from "../store/global-types/customization-types";
import { DeploymentConfigInfo } from "../store/appConfig/appDeploymentInfoTypes";
import { WorkspaceLabeling } from "../store/appVersion/labeling";
import { AppVersionInfo } from "../store/appVersion/appVersionTypes";
import { BackupInfo } from "../store/backup/backup-types";
import { SaveResult } from "./save-response";

/** Query parameter used by backend API to specify caller's session ID. */
export const QUERY_PARAM_SESSION_ID = 'sessionId';

export type JsonValue = string | string[] | number | undefined;

export type LabelJsonWrapper = { modelType: ModelType, labeling: any };

export type CombinedVersionInfoJson = {
    labeling: LabelJsonWrapper[],
    backendVersion: any,
};

/**
 * Generic backend client API wrapper interface. All backend
 * client classes should implement this interface.
 * 
 * Note that the application does not support changing the used backend client
 * on the fly. Each different backend client should have their own build (and
 * deployment, if relevant) of the entire application.
 */
export default interface BackendApiInterface {
    /** One-time initialization of the backend client with the application. */
    init(dispatch: Dispatch, deploymentInfo: DeploymentConfigInfo, appVersion: AppVersionInfo | null): void,

    fetchContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    saveContouringCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult>,
    deleteContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean>,
    fetchActiveModelsAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<any>,
    fetchDefaultConfigurationForContouringModelAsync(modelName: string): Promise<any>,

    fetchDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    saveDoseCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult>,
    deleteDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean>,
    fetchDefaultConfigurationForDoseModelAsync(modelName: string): Promise<any>,

    fetchAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    saveAdaptCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult>,
    deleteAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean>,
    fetchDefaultConfigurationForAdaptModelAsync(modelName: string): Promise<any>,

    fetchImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    saveImageCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult>,
    deleteImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean>,
    fetchDefaultConfigurationForImageModelAsync(modelName: string): Promise<any>,

    fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any>,
    saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean>,
    regenerateCredentialsAsync(appClient: MVisionAppClient | undefined, sessionID: string): Promise<any>,
    tokenAssociationApproveAsync(appClient: MVisionAppClient | undefined, token: string, sessionID: string): Promise<string | null>,
    resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean>,
    deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean>,
    saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean>,
    fetchLabelingAsync(modelTypes: ModelType[]): Promise<CombinedVersionInfoJson>,
    fetchWorkspaceLabelingAsync(): Promise<WorkspaceLabeling>,
    fetchClientListAsync(applicationsOnly?: boolean): Promise<any>,
    fetchAccessRightsAsync(): Promise<any>,
    exportCustomizationPdfAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<Blob>,

    fetchBackupsAsync(appClient: MVisionAppClient | undefined, modelType: string): Promise<BackupInfo[]>;
    downloadBackupAsync(appClient: MVisionAppClient | undefined, modelType: string, backupFilename: string): Promise<any>;

    getUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<any>;
    saveUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string, jsonValue: string): Promise<boolean>;
    deleteUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<boolean>;

    /** Returns a component that will wrap the entire application */
    getAppWrapperComponent(): (props: React.PropsWithChildren<object>) => JSX.Element,
}

